import React, { CSSProperties, useState, useMemo, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Calculator,
  DollarSign,
  Target,
  AlertTriangle,
} from 'lucide-react';
import { Chart } from 'primereact/chart';
import {
  ProgramBreakdownFinancialIncomeReport,
  StudentPaymentSummary,
} from '../../../../../api/types/reports/finanace/program-breakdown/incomes/program-financial-breakdown-incomes.type';
import {
  YearlyProgramExpenseReport,
  CourseFinancialData,
} from '../../../../../api/types/reports/finanace/program-breakdown/incomes/program-financial-breakdown-expanses.type';
import { numberToHebrewLetter } from '../../../../../utils/hebrew-date-utils';
import Colors from '../../../../../utils/colors';
import { LecturerRateDisplayType } from '../../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';

export interface ProgramFinancialBalanceSheetDatamodel {
  incomeData: ProgramBreakdownFinancialIncomeReport;
  expensesData: YearlyProgramExpenseReport;
}

interface YearOption {
  id: number;
  label: string;
}

interface AsdData {
  title: string;
  str: string;
  icon?: React.ReactNode;
  isNegative?: boolean;
}

const Asd: React.FC<AsdData> = ({ str, title, icon, isNegative = false }) => {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 0,
        minHeight: '120px',
        flex: 1,
        gap: '24px',
        border: isNegative ? '2px solid #ef4444' : 'none',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <span
          style={{
            fontSize: '1.1rem',
            color: '#64748b',
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: isNegative ? '#ef4444' : '#0f172a',
            letterSpacing: '-1px',
          }}
        >
          {str}
        </span>
      </div>
      <div
        style={{
          background: isNegative
            ? 'linear-gradient(135deg, #fecaca 0%, #fee2e2 100%)'
            : 'linear-gradient(135deg, #f3f4f6 0%, #e0e7ef 100%)',
          borderRadius: '50%',
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        }}
      >
        {icon}
      </div>
    </div>
  );
};

// Custom Year Filter Component
const YearFilterChips: React.FC<{
  yearOptions: YearOption[];
  selectedYears: number[];
  onToggleYear: (yearId: number) => void;
}> = ({ yearOptions, selectedYears, onToggleYear }) => {
  const chipStyle = (isSelected: boolean): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    margin: '4px',
    borderRadius: '20px',
    border: isSelected ? 'none' : '2px solid #e2e8f0',
    background: isSelected
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : '#fff',
    color: isSelected ? '#fff' : '#64748b',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: isSelected
      ? '0 4px 15px rgba(102, 126, 234, 0.3)'
      : '0 2px 8px rgba(0,0,0,0.06)',
    transform: isSelected ? 'translateY(-1px)' : 'translateY(0)',
    userSelect: 'none' as const,
  });

  const containerStyle: CSSProperties = {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    direction: 'rtl',
    textAlign: 'right',
  };

  const headerStyle: CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
    textAlign: 'right',
    direction: 'rtl',
  };

  const chipsContainerStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
    direction: 'rtl',
    textAlign: 'right',
  };

  const allSelectedButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '4px',
    borderRadius: '16px',
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    color: '#475569',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none' as const,
    direction: 'rtl',
  };

  const handleSelectAll = () => {
    if (selectedYears.length === yearOptions.length) {
      yearOptions.forEach((year) => {
        if (selectedYears.includes(year.id)) {
          onToggleYear(year.id);
        }
      });
    } else {
      yearOptions.forEach((year) => {
        if (!selectedYears.includes(year.id)) {
          onToggleYear(year.id);
        }
      });
    }
  };

  const isAllSelected = selectedYears.length === yearOptions.length;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>סינון לפי שנים</div>
      <div style={chipsContainerStyle}>
        <div
          style={allSelectedButtonStyle}
          onClick={handleSelectAll}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f8fafc';
          }}
        >
          {isAllSelected ? 'בטל הכל' : 'בחר הכל'}
        </div>
        {yearOptions.map((year) => {
          const isSelected = selectedYears.includes(year.id);
          return (
            <div
              key={year.id}
              style={chipStyle(isSelected)}
              onClick={() => onToggleYear(year.id)}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 8px rgba(0,0,0,0.06)';
                }
              }}
            >
              {year.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ProgramFinancialBalanceSheet: React.FC<
  ProgramFinancialBalanceSheetDatamodel
> = ({ incomeData, expensesData }) => {
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [chartData1, setChartData1] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  // Create year options from both income and expense data
  const yearOptions: YearOption[] = useMemo(() => {
    const incomeYears = incomeData ? Object.keys(incomeData).map(Number) : [];
    const expenseYears = expensesData
      ? Object.keys(expensesData).map(Number)
      : [];
    const allYears = Array.from(
      new Set([...incomeYears, ...expenseYears]),
    ).sort();

    return allYears.map((year) => ({
      id: year,
      label:
        year === -1
          ? 'קורסים ביתרה לשיבוץ'
          : `שנה ${numberToHebrewLetter(year)}׳`,
    }));
  }, [incomeData, expensesData]);

  // Initialize selected years to include all available years
  React.useEffect(() => {
    if (yearOptions.length > 0 && selectedYears.length === 0) {
      setSelectedYears(yearOptions.map((year) => year.id));
    }
  }, [yearOptions]);

  const toggleYear = (yearId: number) => {
    setSelectedYears((prev) =>
      prev.includes(yearId)
        ? prev.filter((id) => id !== yearId)
        : [...prev, yearId],
    );
  };

  // Calculate balance sheet data
  const balanceData = useMemo(() => {
    // Filter income data
    const filteredIncomeData: ProgramBreakdownFinancialIncomeReport = {};
    if (incomeData) {
      selectedYears.forEach((year) => {
        if (incomeData[year]) {
          filteredIncomeData[year] = incomeData[year];
        }
      });
    }

    // Filter expense data
    const filteredExpensesData: YearlyProgramExpenseReport = {};
    if (expensesData) {
      selectedYears.forEach((year) => {
        if (expensesData[year]) {
          filteredExpensesData[year] = expensesData[year];
        }
      });
    }

    // Calculate income totals
    let totalIncomeExpected = 0;
    let totalIncomeActual = 0;
    let totalIncomeUnpaid = 0;

    if (filteredIncomeData) {
      Object.values(filteredIncomeData)
        .flat()
        .forEach((student: StudentPaymentSummary) => {
          student.paymentServices.forEach((service) => {
            totalIncomeExpected += service.price || 0;
            const paid =
              service.userPayments?.reduce(
                (sum, payment) => sum + payment.paymentAmount,
                0,
              ) || 0;
            totalIncomeActual += paid;
            totalIncomeUnpaid += (service.price || 0) - paid;
          });
        });
    }

    // Calculate expense totals
    let totalExpensesExpected = 0;
    let totalExpensesActual = 0;
    let totalExpensesUnspent = 0;

    Object.values(filteredExpensesData)
      .flat()
      .forEach((course: CourseFinancialData) => {
        totalExpensesExpected += course.expectedExpenses;
        totalExpensesActual += course.actualExpenses;
      });

    /*if (filteredExpensesData) {
      Object.values(filteredExpensesData)
        .flat()
        .forEach((course: CourseFinancialData) => {
          course.lectures.forEach((lecture) => {
            // Calculate expected expenses (planned amount)
            if (lecture.endTime && lecture.startTime) {
              const plannedDuration =
                (new Date(lecture.endTime).getTime() -
                  new Date(lecture.startTime).getTime()) /
                (1000 * 60 * 60);

              const ratePerHour =
                lecture.displayType === LecturerRateDisplayType.AcademicHour
                  ? lecture.rate * (60 / 45) // Convert academic hour rate to hourly rate
                  : lecture.rate;

              totalExpensesExpected += plannedDuration * ratePerHour;
            }

            // Calculate actual expenses
            if (lecture.actualStartAt && lecture.actualEndAt) {
              const actualDuration =
                (new Date(lecture.actualEndAt).getTime() -
                  new Date(lecture.actualStartAt).getTime()) /
                (1000 * 60 * 60);

              const ratePerHour =
                lecture.displayType === LecturerRateDisplayType.AcademicHour
                  ? lecture.rate * (60 / 45) // Convert academic hour rate to hourly rate
                  : lecture.rate;

              totalExpensesActual += actualDuration * ratePerHour;
            }
          });
        });
    }*/

    totalExpensesUnspent = totalExpensesExpected - totalExpensesActual;

    // Calculate balances
    const expectedBalance = totalIncomeExpected - totalExpensesExpected;
    const actualBalance = totalIncomeActual - totalExpensesActual;
    const projectedBalance =
      totalIncomeExpected - totalIncomeUnpaid - totalExpensesExpected;

    return {
      income: {
        expected: totalIncomeExpected,
        actual: totalIncomeActual,
        unpaid: totalIncomeUnpaid,
      },
      expenses: {
        expected: totalExpensesExpected,
        actual: totalExpensesActual,
        unspent: totalExpensesUnspent,
      },
      balance: {
        expected: expectedBalance,
        actual: actualBalance,
        projected: projectedBalance,
      },
    };
  }, [incomeData, expensesData, selectedYears]);

  // Set up charts
  useEffect(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue(
      '--text-color-secondary',
    );
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    // Single Chart: Income, Expenses, Balance with Expected vs Actual
    const consolidatedChartData = {
      labels: ['הכנסות', 'הוצאות' /*, 'מאזן'*/],
      datasets: [
        {
          type: 'bar',
          label: 'צפוי',
          backgroundColor: [
            Colors.expectedIncomesBackground,
            Colors.expectedExpensesBackground,
          ],
          borderColor: [
            Colors.expectedIncomesBorder,
            Colors.expectedExpensesBorder,
          ],
          borderWidth: 1,
          data: [
            balanceData.income.expected,
            balanceData.expenses.expected,
            /*Math.abs(balanceData.balance.expected),*/
          ],
        },
        {
          type: 'bar',
          label: 'בפועל',
          backgroundColor: [
            Colors.actualIncomesBackground,
            Colors.actualExpensesBackground,
          ],
          borderColor: [
            Colors.actualIncomesBorder,
            Colors.actualExpensesBorder,
          ],
          borderWidth: 1,
          data: [
            balanceData.income.actual,
            balanceData.expenses.actual,
            /*Math.abs(balanceData.balance.actual),*/
          ],
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      datasets: {
        bar: {
          categoryPercentage: 0.1, // Use full category width
          barPercentage: 2.5, // Use full bar width
        },
      },
      plugins: {
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: function (context: any) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }

              // For balance column (index 2), show original sign
              if (context.dataIndex === 2) {
                const originalValue =
                  context.dataset.label === 'צפוי'
                    ? balanceData.balance.expected
                    : balanceData.balance.actual;
                const sign = originalValue >= 0 ? '+' : '';
                label += `${sign}${originalValue.toLocaleString()}₪`;
              } else {
                label += `${context.parsed.y.toLocaleString()}₪`;
              }

              return label;
            },
          },
        },
        legend: {
          labels: {
            color: textColor,
            usePointStyle: true,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
            callback: function (value: any) {
              return `${value.toLocaleString()}₪`;
            },
          },
          grid: {
            color: surfaceBorder,
          },
        },
      },
      barPercentage: 0.6,
      categoryPercentage: 0.8,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
    };

    setChartData1(consolidatedChartData);
    setChartOptions(options);
  }, [balanceData]);

  return (
    <div style={{ width: '100%' }}>
      {/* Year Filter */}
      {yearOptions.length > 0 && (
        <YearFilterChips
          yearOptions={yearOptions}
          selectedYears={selectedYears}
          onToggleYear={toggleYear}
        />
      )}

      {/* Balance Sheet Summary */}
      <div style={{ marginBottom: '48px' }}>
        <h2
          style={{
            textAlign: 'right',
            marginBottom: '24px',
            color: '#374151',
            fontSize: '1.5rem',
            fontWeight: 600,
          }}
        >
          מאזן כספי - השוואה בין הכנסות להוצאות
        </h2>

        {/* Income Section */}
        <h3
          style={{
            textAlign: 'right',
            marginBottom: '16px',
            color: '#059669',
            fontSize: '1.2rem',
            fontWeight: 600,
          }}
        >
          הכנסות
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          <Asd
            str={`${balanceData.income.expected.toLocaleString()}₪`}
            title={'הכנסות צפויות'}
            icon={<Target color="#059669" size={32} />}
          />
          <Asd
            str={`${balanceData.income.actual.toLocaleString()}₪`}
            title={'הכנסות בפועל'}
            icon={<TrendingUp color="#10b981" size={32} />}
          />
          <Asd
            str={`${balanceData.income.unpaid.toLocaleString()}₪`}
            title={'טרם שולם'}
            icon={<AlertTriangle color="#f59e0b" size={32} />}
          />
        </div>

        {/* Expenses Section */}
        <h3
          style={{
            textAlign: 'right',
            marginBottom: '16px',
            color: '#dc2626',
            fontSize: '1.2rem',
            fontWeight: 600,
          }}
        >
          הוצאות
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          <Asd
            str={`${balanceData.expenses.expected.toLocaleString()}₪`}
            title={'הוצאות מתוכננות'}
            icon={<Target color="#dc2626" size={32} />}
          />
          <Asd
            str={`${balanceData.expenses.actual.toLocaleString()}₪`}
            title={'הוצאות בפועל'}
            icon={<TrendingDown color="#ef4444" size={32} />}
          />
          <Asd
            str={`${balanceData.expenses.unspent.toLocaleString()}₪`}
            title={'טרם בוצע'}
            icon={<Calculator color="#8b5cf6" size={32} />}
          />
        </div>

        {/* Balance Section */}
        <h3
          style={{
            textAlign: 'right',
            marginBottom: '16px',
            color: '#374151',
            fontSize: '1.2rem',
            fontWeight: 600,
          }}
        >
          מאזן
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          <Asd
            str={`${balanceData.balance.expected >= 0 ? '+' : ''}${balanceData.balance.expected.toLocaleString()}₪`}
            title={'מאזן צפוי'}
            icon={
              <DollarSign
                color={
                  balanceData.balance.expected >= 0 ? '#059669' : '#ef4444'
                }
                size={32}
              />
            }
            isNegative={balanceData.balance.expected < 0}
          />
          <Asd
            str={`${balanceData.balance.actual >= 0 ? '+' : ''}${balanceData.balance.actual.toLocaleString()}₪`}
            title={'מאזן בפועל'}
            icon={
              <Calculator
                color={balanceData.balance.actual >= 0 ? '#10b981' : '#ef4444'}
                size={32}
              />
            }
            isNegative={balanceData.balance.actual < 0}
          />
          {/*<Asd
            str={`${balanceData.balance.projected >= 0 ? '+' : ''}${balanceData.balance.projected.toLocaleString()}₪`}
            title={'מאזן מוקרן'}
            icon={
              <TrendingUp
                color={
                  balanceData.balance.projected >= 0 ? '#22c55e' : '#f59e0b'
                }
                size={32}
              />
            }
            isNegative={balanceData.balance.projected < 0}
          />*/}
        </div>
      </div>

      {/* Income vs Expenses Chart */}
      <div style={{ marginBottom: '2rem' }}>
        <h3
          style={{ textAlign: 'right', marginBottom: '1rem', color: '#374151' }}
        >
          מבט כללי על המאזן הכספי
        </h3>
        <Chart type="bar" data={chartData1} options={chartOptions} />
      </div>
    </div>
  );
};
